@if (vm$ | async; as vm) {
  <div [style.width]="'100%'">
    <h1 mat-dialog-title>
      <div class="flex flex-1">
        <div class="flex flex-1 content-center items-center">
          @switch (vm.action.status) {
            @case ('voided') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> block </mat-icon>
            }
            @case ('complete-passed') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> thumb_up </mat-icon>
            }
            @case ('complete-failed') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> thumb_down </mat-icon>
            }
            @case ('complete') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> pending </mat-icon>
            }
            @case ('complete-near-pass') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> call_missed </mat-icon>
            }
            @case ('complete-near-pass-accepted') {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> thumb_up </mat-icon>
            }
            @default {
              <mat-icon fontSet="material-icons-outlined" [class]="vm.action.status"> send </mat-icon>
            }
          }
          <span data-test-id="screener-result-dialog-title">&nbsp; Screener results </span>
        </div>
        <button mat-icon-button mat-dialog-close>
          <mat-icon data-test-id="screener-result-dialog-close-button">close</mat-icon>
        </button>
      </div>
    </h1>
    <div mat-dialog-content>
      <mat-form-field appearance="outline" class="notes-field h-full w-full min-h-full min-w-full">
        <mat-label data-test-id="screener-result-dialog-notes-label">Notes</mat-label>
        <textarea
          matInput
          placeholder="Notes"
          data-test-id="screener-result-dialog-notes-input"
          [style.resize]="'none'"
          [formControl]="screenerNotes"
          [style.height]="'100px'"
        ></textarea>
      </mat-form-field>

      <div class="marking-overview" data-test-id="screener-result-dialog-marking">
        <div class="marking-header">
          <div class="marking-heading">AI marking</div>
          @if (vm.overview.status === 'marked') {
            <div class="marking-meta">
              <div class="marking-overall" data-test-id="screener-result-dialog-marking-verdict">
                @if (vm.overview.overallVerdict === 'pass') {
                  <mat-icon fontSet="material-icons-outlined" class="complete-passed">check</mat-icon>
                  <span class="complete-passed">Pass</span>
                } @else if (vm.overview.overallVerdict === 'hold') {
                  <mat-icon fontSet="material-icons-outlined" class="complete-hold">help_outline</mat-icon>
                  <span class="complete-hold">Hold</span>
                } @else if (vm.overview.overallVerdict === 'failed') {
                  <mat-icon fontSet="material-icons-outlined" class="complete-failed">close</mat-icon>
                  <span class="complete-failed">Failed</span>
                }
              </div>
              <div class="marking-counts" data-test-id="screener-result-dialog-marking-counts">
                <span>{{ vm.overview.total }} questions</span>
                <span class="complete-passed">{{ vm.overview.passCount }} pass</span>
                <span class="complete-hold">{{ vm.overview.holdCount }} hold</span>
                <span class="complete-failed">{{ vm.overview.failCount }} fail</span>
              </div>
            </div>
          }
        </div>
        @switch (vm.overview.status) {
          @case ('loading') {
            <div class="marking-status" data-test-id="screener-result-dialog-marking-loading">
              <mat-spinner [diameter]="20"></mat-spinner>
              <span>Loading marking…</span>
            </div>
          }
          @case ('pending') {
            <div class="marking-status muted" data-test-id="screener-result-dialog-marking-pending">
              <mat-icon fontSet="material-icons-outlined">pending</mat-icon>
              <span>AI marking not available yet</span>
            </div>
          }
          @case ('error') {
            <div class="marking-status muted" data-test-id="screener-result-dialog-marking-error">
              <mat-icon fontSet="material-icons-outlined">error_outline</mat-icon>
              <span>Couldn't load AI marking</span>
            </div>
          }
          @case ('marked') {
            @if (vm.overview.summary?.length) {
              <ul class="marking-summary" data-test-id="screener-result-dialog-marking-summary">
                @for (line of vm.overview.summary; track $index) {
                  <li>{{ line }}</li>
                }
              </ul>
            }
          }
        }
      </div>

      <div class="accordion-toolbar">
        <button
          mat-stroked-button
          type="button"
          data-test-id="screener-result-dialog-copy-answers-button"
          [disabled]="!vm.questions.length"
          (click)="copyQuestionsAndAnswers(vm.questions)"
        >
          <mat-icon fontSet="material-icons-outlined">content_copy</mat-icon> &nbsp;
          <span>Copy questions &amp; answers</span>
        </button>
      </div>

      <div class="screener-questions">
        @for (question of vm.questions; track question.id) {
          <div
            class="question-card"
            data-test-id="screener-result-dialog-question-panel"
            [class.pass]="question.verdict === 'pass'"
            [class.hold]="question.verdict === 'hold'"
            [class.fail]="question.verdict === 'failed'"
          >
            <div class="question-card-header">
              <span class="q" data-test-id="screener-result-dialog-full-question">{{ question.question }}</span>
            </div>

            <div class="answer-block">
              <div class="answer-text" data-test-id="screener-result-dialog-answer">{{ question.answer }}</div>
            </div>

            @if (question.reasoning) {
              <details class="ai-feedback" [open]="question.verdict === 'failed' || question.verdict === 'hold'">
                <summary class="answer-label" data-test-id="screener-result-dialog-marking-reasoning-toggle">
                  <mat-icon fontSet="material-icons-outlined">auto_awesome</mat-icon>
                  <span>AI feedback</span>
                  <mat-icon class="chevron" fontSet="material-icons-outlined">expand_more</mat-icon>
                </summary>
                <div class="marking-reasoning" data-test-id="screener-result-dialog-marking-reasoning">
                  {{ question.reasoning }}
                </div>
              </details>
            }
          </div>
        }
      </div>
    </div>
    <div mat-dialog-actions>
      <div class="flex flex-1 justify-between gap-2">
        <button
          mat-stroked-button
          class="failedBtn"
          data-test-id="screener-result-dialog-void-screener-button"
          [disabled]="isLoading || vm.action.status === 'voided'"
          (click)="update(ExpertScreenerActionStatusMap.Voided)"
        >
          <mat-icon fontSet="material-icons-outlined"> block </mat-icon> &nbsp;
          <span data-test-id="screener-result-dialog-void-survey-button-label">Void screener</span>
        </button>
        <button
          mat-stroked-button
          class="failedBtn"
          data-test-id="screener-result-dialog-fail-screener-button"
          [disabled]="isDisabled(vm.action.status) || vm.action.status === 'complete-failed'"
          (click)="update(ExpertScreenerActionStatusMap.CompleteFailed)"
        >
          <mat-icon fontSet="material-icons-outlined"> thumb_down </mat-icon> &nbsp;
          <span data-test-id="screener-result-dialog-fail-screener-button-label">Mark as failed</span>
        </button>
        <button
          mat-stroked-button
          class="nearPassBtn"
          data-test-id="screener-result-dialog-near-pass-screener-button"
          [disabled]="isDisabled(vm.action.status) || vm.action.status === 'complete-near-pass'"
          (click)="update(ExpertScreenerActionStatusMap.CompleteNearPass)"
        >
          <mat-icon fontSet="material-icons-outlined"> flip </mat-icon> &nbsp;
          <span data-test-id="screener-result-dialog-near-pass-screener-button-label">Mark as near pass</span>
        </button>
        <button
          mat-flat-button
          cdkFocusInitial
          color="primary"
          data-test-id="screener-result-dialog-pass-screener-button"
          [disabled]="isDisabled(vm.action.status) || vm.action.status === 'complete-passed'"
          (click)="update(ExpertScreenerActionStatusMap.CompletePassed)"
        >
          <mat-icon fontSet="material-icons-outlined"> thumb_up </mat-icon> &nbsp;
          <span data-test-id="screener-result-dialog-pass-screener-button-label">Mark as passed</span>
        </button>
        <button
          mat-flat-button
          cdkFocusInitial
          color="primary"
          data-test-id="screener-result-dialog-save-notes-button"
          [disabled]="isLoading || !screenerNotes.dirty"
          (click)="update()"
        >
          <mat-icon fontSet="material-icons-outlined"> check </mat-icon> &nbsp;
          <span data-test-id="screener-result-dialog-save-notes-button-label">Save notes</span>
        </button>
      </div>
    </div>
  </div>
} @else {
  <div class="flex justify-center content-center items-center flex-1 h-full">
    <mat-spinner></mat-spinner>
  </div>
}
